// Static set dressing drawn once per room: catwalk supports under one-way platforms
// and the pulsing glow + sparks over hazard strips. Cosmetic only (Math.random allowed
// for flicker).

import Phaser from 'phaser';
import { DEPTH, TILE } from '../../config/constants';
import { COLORS, mix, shade } from '../../config/palette';
import type { SectorDef } from '../../core/sector';
import { cellHash, stageKit, type StageKit } from '../../art/world/stage';
import { CELL } from './template';
import type { RoomMap } from './tilemap';
import { pixelTex, softTex } from './textures';

export const HAZARD_COLOR: Record<SectorDef['hazard'], number> = {
  electric: 0xff4f7a,
  molten: 0xff7a1a,
  void: 0xc86bff,
};

export class Dressing {
  private objects: Phaser.GameObjects.GameObject[] = [];
  private hazardGlows: Phaser.GameObjects.Image[] = [];
  private sparks: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private sparkTimer = 0;
  private t = 0;

  constructor(
    private scene: Phaser.Scene,
    private sector: SectorDef,
    private map: RoomMap,
  ) {
    this.kit = map.stageArt ? stageKit(scene, sector.biome) : null;
    if (this.kit) {
      this.paintCatwalks(this.kit);
      this.paintHazards(this.kit);
    } else this.drawSupports();
    this.buildHazards();
  }

  private kit: StageKit | null = null;

  /** Support columns under a catwalk run that hang over solid ground within reach (tile x, ground row). */
  private legs(run: { x0: number; x1: number; y: number }): { tx: number; gy: number }[] {
    const m = this.map;
    const len = run.x1 - run.x0 + 1;
    const cols = len >= 9 ? [run.x0, Math.floor((run.x0 + run.x1) / 2), run.x1] : len >= 2 ? [run.x0, run.x1] : [run.x0];
    const out: { tx: number; gy: number }[] = [];
    for (const tx of cols) {
      let gy = run.y + 1;
      while (gy < m.rows && m.kindAtTile(tx, gy) === CELL.EMPTY) gy++;
      const depth = gy - run.y - 1;
      if (gy < m.rows && depth >= 1 && depth <= 7) out.push({ tx, gy });
    }
    return out;
  }

  /**
   * Painted catwalks: one piece per tile - end, middle variants, end - each middle scaled to exactly
   * one tile wide so they join seamlessly; the deck line sits on the tile top (the collision surface).
   * Painted struts hold runs up from the ground below.
   */
  private paintCatwalks(kit: StageKit): void {
    const sc = this.scene;
    const strut = kit.img('strut');
    const fs = 24 / strut.width;
    const mids = ['deck_m0', 'deck_m1'] as const;
    // Scale from the middles (exactly one tile wide); end caps share it and overhang outward.
    const f = TILE / ((kit.img('deck_m0').width + kit.img('deck_m1').width) / 2);
    for (const run of this.map.platformRuns) {
      const y = run.y * TILE;
      for (const leg of this.legs(run)) {
        const cx = leg.tx * TILE + TILE / 2;
        const top = y + 10;
        const h = leg.gy * TILE - top;
        this.track(sc.add.tileSprite(cx, top, strut.width, h / fs, kit.key('strut')).setOrigin(0.5, 0).setScale(fs).setDepth(DEPTH.WORLD_BACK + 2));
      }
      for (let tx = run.x0; tx <= run.x1; tx++) {
        const single = run.x0 === run.x1;
        const piece = !single && tx === run.x0 ? 'deck_l' : !single && tx === run.x1 ? 'deck_r' : mids[cellHash(tx, run.y) < 0.5 ? 0 : 1];
        this.placePiece(kit, piece, tx, y, f, DEPTH.TILES + 0.5);
      }
    }
  }

  /** Painted hazard strips: one piece per tile (emitter ends, middle variants), surface on the tile top. */
  private paintHazards(kit: StageKit): void {
    const mids = ['haz_m0', 'haz_m1'] as const;
    const f = TILE / ((kit.img('haz_m0').width + kit.img('haz_m1').width) / 2);
    for (const run of this.map.hazardRuns) {
      const y = run.y * TILE + 6;
      for (let tx = run.x0; tx <= run.x1; tx++) {
        const single = run.x0 === run.x1;
        const piece = !single && tx === run.x0 ? 'haz_l' : !single && tx === run.x1 ? 'haz_r' : mids[cellHash(tx, run.y) < 0.5 ? 0 : 1];
        this.placePiece(kit, piece, tx, y, f, DEPTH.TILES + 0.3);
      }
    }
  }

  /** One strip piece on tile column tx: middles fill the tile exactly, ends overhang outward. */
  private placePiece(
    kit: StageKit,
    piece: 'deck_l' | 'deck_m0' | 'deck_m1' | 'deck_r' | 'haz_l' | 'haz_m0' | 'haz_m1' | 'haz_r',
    tx: number,
    y: number,
    f: number,
    depth: number,
  ): void {
    const img = kit.img(piece);
    const lift = (kit.pieces[piece]?.lift ?? 0) * f;
    const x = tx * TILE;
    const o = this.scene.add.image(0, y - lift, kit.key(piece)).setOrigin(0, 0).setDepth(depth);
    if (piece.endsWith('_l')) o.setScale(f).setX(x + TILE - img.width * f);
    else if (piece.endsWith('_r')) o.setScale(f).setX(x);
    else o.setScale(TILE / img.width, f).setX(x);
    this.track(o);
  }

  private track<T extends Phaser.GameObjects.GameObject>(o: T): T {
    this.objects.push(o);
    return o;
  }

  /** Truss legs under catwalks that hang over solid ground within reach. */
  private drawSupports(): void {
    const m = this.map;
    const g = this.track(this.scene.add.graphics().setDepth(DEPTH.WORLD_BACK + 2));
    const steel = mix(COLORS.steel1, this.sector.palette.fog, 0.2);
    const edge = shade(steel, 1.6);
    for (const run of m.platformRuns) {
      const len = run.x1 - run.x0 + 1;
      const legs = len >= 9 ? [run.x0, Math.floor((run.x0 + run.x1) / 2), run.x1] : len >= 2 ? [run.x0, run.x1] : [run.x0];
      for (const tx of legs) {
        let gy = run.y + 1;
        while (gy < m.rows && m.kindAtTile(tx, gy) === CELL.EMPTY) gy++;
        const depth = gy - run.y - 1;
        if (gy >= m.rows || depth < 1 || depth > 7) continue;
        const cx = tx * TILE + TILE / 2;
        const top = run.y * TILE + 20;
        const bottom = gy * TILE;
        g.fillStyle(steel, 1);
        g.fillRect(cx - 9, top, 4, bottom - top);
        g.fillRect(cx + 5, top, 4, bottom - top);
        g.fillStyle(edge, 0.5);
        g.fillRect(cx - 9, top, 1, bottom - top);
        g.fillRect(cx + 5, top, 1, bottom - top);
        // Cross bracing every tile.
        g.lineStyle(2, steel, 1);
        for (let y = top; y + 40 <= bottom; y += 44) {
          g.lineBetween(cx - 7, y, cx + 7, y + 40);
          g.lineBetween(cx + 7, y, cx - 7, y + 40);
        }
        g.fillStyle(shade(steel, 0.6), 1);
        g.fillRect(cx - 13, bottom - 6, 26, 6);
      }
    }
  }

  private buildHazards(): void {
    if (this.map.hazardRuns.length === 0) return;
    const color = HAZARD_COLOR[this.sector.hazard];
    const soft = softTex(this.scene);
    for (const run of this.map.hazardRuns) {
      const x = run.x0 * TILE;
      const w = (run.x1 - run.x0 + 1) * TILE;
      const y = run.y * TILE;
      this.hazardGlows.push(
        this.track(
          this.scene.add
            .image(x + w / 2, y + 6, soft)
            .setDisplaySize(w + 60, 90)
            .setTint(color)
            .setBlendMode(Phaser.BlendModes.ADD)
            .setAlpha(0.35)
            .setDepth(DEPTH.TILES + 1),
        ),
      );
    }
    this.sparks = this.track(
      this.scene.add
        .particles(0, 0, pixelTex(this.scene), {
          emitting: false,
          lifespan: { min: 200, max: this.sector.hazard === 'molten' ? 900 : 420 },
          speed: { min: 60, max: this.sector.hazard === 'electric' ? 340 : 160 },
          angle: { min: 220, max: 320 },
          gravityY: this.sector.hazard === 'void' ? -120 : 700,
          scale: { start: 0.8, end: 0 },
          alpha: { start: 1, end: 0 },
          tint: [color, mix(color, 0xffffff, 0.5)],
          blendMode: 'ADD',
        })
        .setDepth(DEPTH.FX),
    );
  }

  update(dt: number, cam: Phaser.Cameras.Scene2D.Camera): void {
    this.t += dt;
    if (this.hazardGlows.length > 0) {
      const pulse = 0.28 + 0.12 * Math.sin(this.t * 5.3) + (Math.random() < 0.03 ? 0.15 : 0);
      for (const g of this.hazardGlows) g.setAlpha(pulse);
      this.sparkTimer -= dt;
      if (this.sparks && this.sparkTimer <= 0) {
        this.sparkTimer = 0.06 + Math.random() * 0.12;
        const view = cam.worldView;
        const run = this.map.hazardRuns[Math.floor(Math.random() * this.map.hazardRuns.length)];
        const x = (run.x0 + Math.random() * (run.x1 - run.x0 + 1)) * TILE;
        if (x > view.x - 50 && x < view.right + 50) this.sparks.emitParticleAt(x, run.y * TILE + 4, 2 + Math.floor(Math.random() * 3));
      }
    }
  }

  destroy(): void {
    for (const o of this.objects) o.destroy();
    this.objects = [];
    this.hazardGlows = [];
    this.sparks = null;
  }
}
